// EDIT AN EXISTING CUSTOMER'S BILLING DETAILS -- customer-details-on-quote.
//
// Owner decisions, 2026-09-23:
//   * the billing address lives on the CUSTOMER COMPANY record itself. No
//     quote is written by this form.
//   * Sales and Admin edit address, state, GSTIN, e-mail and phone. The NAME
//     is shown and locked. Viewer never sees this form -- and the route
//     refuses a Viewer with a 403 whatever this screen does.
//   * GSTIN format is ADVISORY: an amber note, never a block (same rule as
//     Settings > Company, via lib/gstin.ts).
//   * a GSTIN held by another ACTIVE customer is an amber warning NAMING it,
//     and the person can still save. The check is the backend's, exact at any
//     size -- not the old first-200 scan.
//   * ⚠️ TYPED BY A PERSON. Nothing from a PO is ever copied in; this form
//     has no PO input and the route refuses any key it does not allow.
//
// ⚠️ DRAFTS PRINT WHAT IS SAVED HERE; FINALISED QUOTES DO NOT. A quote finalised
// since 17.0.1.40.0 prints the customer as it was issued (its snapshot), so an
// edit here never changes an issued document. Quotes finalised before that have
// no snapshot and print current details -- no backfill. The form says so.

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import {
  getCustomerAddresses, listIndianStates, updateCustomer,
  type CustomerDetailsEdit, type IndianState,
} from "@/services/api";
import { gstinLooksWrong } from "@/lib/gstin";
import type { CustomerPartner } from "@/types";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

type Form = Omit<CustomerDetailsEdit, "id">;

const EMPTY: Form = {
  street: "", street2: "", city: "", zip: "", state_id: null,
  vat: "", email: "", phone: "",
};

function formFrom(p: CustomerPartner): Form {
  return {
    street: p.street ?? "", street2: p.street2 ?? "", city: p.city ?? "",
    zip: p.zip ?? "", state_id: p.state_id ?? null, vat: p.vat ?? "",
    email: p.email ?? "", phone: p.phone ?? "",
  };
}

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4], display: "block",
};
const INPUT: React.CSSProperties = {
  width: "100%", height: heights.input, padding: `0 ${spacing[10]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  backgroundColor: semanticColors.surface, boxSizing: "border-box",
};
const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON, border: "none", backgroundColor: colors.indigo600,
  // onAccent, never white: tokens.ts says so on the accent itself.
  color: semanticColors.onAccent,
};
const AMBER: React.CSSProperties = {
  padding: `${spacing[8]}px ${spacing[12]}px`, borderRadius: radius.base,
  backgroundColor: semanticColors.warningBg, border: `1px solid ${semanticColors.warningBorder}`,
  color: semanticColors.warningTextOnTint, fontSize: typography.fontSize.sm,
};

export function CustomerDetailsModal({
  entity, onClose, onSaved,
}: {
  /** The customer company a quote names. Only its id and name are trusted;
   *  the details are re-read on open so the form never edits a stale copy. */
  entity: CustomerPartner;
  onClose: () => void;
  /** The saved entity, exactly as the backend now holds it. */
  // state-from-gstin: `gstinNote` when the State disagrees with the GSTIN
  // (saved anyway -- the caller shows it in amber).
  onSaved: (entity: CustomerPartner, gstinNote?: string | null) => void;
}) {
  const [form, setForm] = useState<Form>(EMPTY);
  const [states, setStates] = useState<IndianState[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [duplicates, setDuplicates] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    void Promise.all([getCustomerAddresses(entity.id), listIndianStates()]).then(
      ([fresh, stateList]) => {
        if (!live) return;
        setStates(stateList);
        if (!fresh.ok || !fresh.entity) {
          setLoadError(fresh.error ?? "The customer could not be read.");
        } else {
          setForm(formFrom(fresh.entity));
        }
        setLoading(false);
      },
    );
    return () => { live = false; };
  }, [entity.id]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setError("");
    // A changed GSTIN is a new question: the old warning no longer applies.
    if (key === "vat") setDuplicates([]);
  };

  const save = async (confirmDuplicate: boolean) => {
    setBusy(true);
    setError("");
    const result = await updateCustomer({ id: entity.id, ...form }, confirmDuplicate);
    setBusy(false);
    if (result.duplicates && result.duplicates.length > 0) {
      setDuplicates(result.duplicates);
      return;
    }
    if (!result.ok || !result.entity) {
      setError(result.error ?? "The customer was not saved.");
      return;
    }
    onSaved(result.entity, result.gstinNote ?? null);
  };

  const text = (key: Exclude<keyof Form, "state_id">, label: string, testid: string) => (
    <label style={{ display: "block" }}>
      <span style={LABEL}>{label}</span>
      <input
        data-testid={testid}
        value={form[key] as string}
        onChange={(e) => set(key, e.target.value)}
        disabled={busy || loading}
        style={INPUT}
      />
    </label>
  );

  return (
    <Modal
      testIdPrefix="customer-details"
      title="Customer details"
      subtitle={entity.name}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button data-testid="customer-details-cancel" onClick={onClose} disabled={busy} style={BUTTON}>
            Cancel
          </button>
          {duplicates.length === 0 && (
            <button
              data-testid="customer-details-save"
              onClick={() => void save(false)}
              disabled={busy || loading || loadError !== ""}
              style={{ ...PRIMARY, opacity: busy || loading ? 0.6 : 1 }}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          )}
        </>
      }
    >
      {loading && (
        <div data-testid="customer-details-loading" style={{ color: semanticColors.textSecondary }}>
          Reading the customer…
        </div>
      )}
      {loadError !== "" && (
        <div data-testid="customer-details-load-error" style={{ ...AMBER }}>{loadError}</div>
      )}
      {!loading && loadError === "" && (
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[12] }}>
          <label style={{ display: "block" }}>
            <span style={LABEL}>Name</span>
            {/* LOCKED. A customer's name is not edited from here. */}
            <input data-testid="customer-details-name" value={entity.name} readOnly disabled
                   title="The customer's name is not changed here."
                   // app review P2-10: looks read-only, because it is.
                   style={{ ...INPUT, backgroundColor: semanticColors.panelBg,
                            color: semanticColors.textSecondary, cursor: "not-allowed" }} />
          </label>
          {text("street", "Address line 1", "customer-details-street")}
          {text("street2", "Address line 2", "customer-details-street2")}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[12] }}>
            {text("city", "City", "customer-details-city")}
            {text("zip", "PIN code", "customer-details-zip")}
          </div>
          <label style={{ display: "block" }}>
            <span style={LABEL}>State</span>
            <select
              data-testid="customer-details-state"
              value={form.state_id ?? ""}
              onChange={(e) => set("state_id", e.target.value === "" ? null : Number(e.target.value))}
              disabled={busy}
              style={INPUT}
            >
              <option value="">—</option>
              {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          {text("vat", "GSTIN", "customer-details-gstin")}
          {/* ADVISORY ONLY. Same rule and same wording as Settings > Company. */}
          {gstinLooksWrong(form.vat) && (
            <div data-testid="customer-details-gstin-warning" style={AMBER}>
              Does not look like a 15-character GSTIN. It will still be saved.
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[12] }}>
            {text("email", "E-mail", "customer-details-email")}
            {text("phone", "Phone", "customer-details-phone")}
          </div>
          {duplicates.length > 0 && (
            <div data-testid="customer-details-duplicate" style={{ ...AMBER, display: "flex", flexDirection: "column", gap: spacing[8] }}>
              <span>
                This GSTIN is already held by {duplicates.map((d) => d.name).join(", ")}.
                Save it on this customer too?
              </span>
              <div style={{ display: "flex", gap: spacing[8] }}>
                <button data-testid="customer-details-save-anyway" onClick={() => void save(true)} disabled={busy} style={BUTTON}>
                  Save anyway
                </button>
                <button data-testid="customer-details-duplicate-back" onClick={() => setDuplicates([])} disabled={busy} style={BUTTON}>
                  Go back
                </button>
              </div>
            </div>
          )}
          {error !== "" && (
            <div data-testid="customer-details-error" style={{
              ...AMBER, backgroundColor: semanticColors.destructiveBg,
              border: `1px solid ${semanticColors.destructiveBorder}`, color: semanticColors.destructive,
            }}>
              {error}
            </div>
          )}
          {/* customer-snapshot-at-finalise: the owner's wording, verbatim. */}
          <div data-testid="customer-details-snapshot-note" style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
            Finalised quotes keep the details they were issued with.
          </div>
        </div>
      )}
    </Modal>
  );
}
